/* GWP scratch to reveal (sections/cs-gwp-scratch.liquid)
   <cs-gwp-scratch>: follows the cart to show how far it is from the gift's
   threshold, sorts the suggested products, and runs the scratch card.

   The qualifying total matches assets/automatic-gwp.js: the cart total after
   discounts minus every configured gift's line. A reveal is remembered in
   localStorage per gift, so the card stays revealed on later visits.

   The product row loops, like the Shop bundle steps: a copy of every shown
   card sits on both ends, and when the row comes to rest on a copy it jumps
   (instantly, so nothing moves on screen) to the matching real card. The
   copies are rebuilt whenever the cart re-sorts or hides suggestions.
*/

if (!customElements.get('cs-gwp-scratch')) {
    class CsGwpScratch extends HTMLElement {
        // Share of the surface that has to be scratched off before the rest clears
        static REVEAL_AT = 0.5;
        // Brush size in CSS pixels
        static BRUSH_SIZE = 44;

        connectedCallback() {
            this.threshold = Number(this.dataset.threshold) || 0;
            this.giftVariantId = Number(this.dataset.giftVariantId) || 0;
            this.giftVariantIds = (this.dataset.giftVariantIds || '')
                .split(',')
                .map(Number)
                .filter((id) => id > 0);
            this.isPreview = this.hasAttribute('data-preview');
            this.storageKey = `cs-gwp-revealed:${this.giftVariantId}`;

            this.lockedStatus = this.querySelector('[data-locked-status]');
            this.progressBar = this.querySelector('[data-progress]');
            this.row = this.querySelector('[data-row]');
            this.products = Array.from(this.querySelectorAll('[data-product]'));
            this.nextButton = this.querySelector('[data-next]');
            this.errorMessage = this.querySelector('[data-error]');
            this.scratch = this.querySelector('[data-scratch]');
            this.canvas = this.querySelector('[data-surface]');

            this.nextButton?.addEventListener('click', () => this.showNextProduct());
            this.querySelectorAll('[data-add]').forEach((button) => {
                button.addEventListener('click', () => this.addProduct(button));
            });
            this.row?.addEventListener('scroll', debounce(() => this.onRowSettle(), 120));
            this.buildLoop();
            this.setUpScratch();
            this.updateSurfaceFocus();

            if (this.isPreview) return;

            if (this.dataset.state === 'unlocked' && this.wasRevealed()) this.setState('revealed');

            // Add + Unlock, the cart drawer and the automatic gift all announce
            // cart changes; several can arrive together, so refresh once
            this.refresh = debounce(() => this.updateFromCart(), 250);
            if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.refresh);
            }
            this.onCartRefresh = () => this.refresh();
            document.addEventListener('cart:refresh', this.onCartRefresh);

            this.updateFromCart();
        }

        disconnectedCallback() {
            this.unsubscribe?.();
            document.removeEventListener('cart:refresh', this.onCartRefresh);
            this.resizeObserver?.disconnect();
        }

        /* Cart ----------------------------------------------------------- */

        async updateFromCart() {
            try {
                const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok) throw new Error(`Unable to read the cart (${response.status}).`);
                const cart = await response.json();

                const giftsTotal = cart.items
                    .filter((item) => this.giftVariantIds.includes(item.variant_id))
                    .reduce((total, item) => total + item.final_line_price, 0);
                const eligibleTotal = cart.total_price - giftsTotal;
                const giftInCart = cart.items.some(
                    (item) => item.variant_id === this.giftVariantId && item.properties?._automatic_gwp
                );
                const isUnlocked = giftInCart || (eligibleTotal > 0 && eligibleTotal >= this.threshold);
                const remaining = Math.max(0, this.threshold - eligibleTotal);

                this.updateProgress(isUnlocked ? 100 : (eligibleTotal / this.threshold) * 100, remaining);
                this.updateProducts(cart, remaining);

                if (!isUnlocked) {
                    this.setState('locked');
                } else if (this.dataset.state === 'locked') {
                    this.setState(this.wasRevealed() ? 'revealed' : 'unlocked');
                }
            } catch (error) {
                console.error('Error updating the GWP section from the cart:', error);
            }
        }

        updateProgress(percent, remaining) {
            const value = Math.round(Math.max(0, Math.min(100, percent || 0)));
            if (this.progressBar) {
                this.progressBar.style.setProperty('--pro-progress', `${value}%`);
                this.progressBar.setAttribute('aria-valuenow', value);
            }
            if (this.lockedStatus) {
                this.lockedStatus.innerHTML = this.lockedStatus.dataset.template.replaceAll(
                    '[remaining]',
                    ProCart.formatMoney(remaining)
                );
            }
        }

        // Hides products already in the cart. Those that reach the threshold
        // on their own come first (cheapest first); the rest follow, priciest
        // first, so the closest suggestions lead.
        updateProducts(cart, remaining) {
            if (!this.row) return;
            const productIdsInCart = new Set(cart.items.map((item) => item.product_id));

            const visible = this.products.filter((product) => {
                const isInCart = productIdsInCart.has(Number(product.dataset.productId));
                product.hidden = isInCart;
                return !isInCart;
            });
            const price = (product) => Number(product.dataset.price);
            const qualifying = visible.filter((product) => price(product) >= remaining).sort((a, b) => price(a) - price(b));
            const others = visible.filter((product) => price(product) < remaining).sort((a, b) => price(b) - price(a));

            [...qualifying, ...others, ...this.products.filter((product) => product.hidden)].forEach((product) => {
                this.row.append(product);
            });
            this.buildLoop();
        }

        // Adds a suggestion without opening the cart drawer, so the progress
        // stays in view (the drawer and cart count refresh in place).
        // automatic-gwp.js adds the gift once the cart qualifies.
        async addProduct(button) {
            if (button.getAttribute('aria-disabled') === 'true') return;
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('loading');
            this.showError('');

            try {
                // Adds without opening the drawer; refreshes it in place (assets/pro-cart.js)
                await ProCart.add([{ id: Number(button.dataset.variantId), quantity: 1 }], {
                    openDrawer: false,
                    source: 'cs-gwp-scratch',
                });
                button.textContent = button.dataset.addedLabel || button.textContent;
            } catch (error) {
                console.error('Error adding a GWP suggestion to the cart:', error);
                this.showError(error.message || ProCart.ERROR_MESSAGE);
                button.removeAttribute('aria-disabled');
            } finally {
                button.classList.remove('loading');
            }
        }

        showError(message) {
            if (!this.errorMessage) return;
            this.errorMessage.textContent = message;
            this.errorMessage.hidden = !message;
        }

        /* Looping row --------------------------------------------------- */
        // The row is reversed (.pro-scroll-row--reverse): the first card sits
        // against the arrow on the right and later cards run off to the left.
        // A card's resting position is the scrollLeft that puts its right edge
        // at the arrow.

        buildLoop() {
            if (!this.row) return;
            this.row.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
            this.realProducts = Array.from(this.row.querySelectorAll('[data-product]:not([hidden])'));

            if (this.realProducts.length > 1) {
                const firstReal = this.realProducts[0];
                this.realProducts.forEach((product, index) => {
                    firstReal.before(this.cloneProduct(product, index));
                    this.row.append(this.cloneProduct(product, index));
                });
            }

            // Start on the first real card, against the arrow
            requestAnimationFrame(() => this.jumpTo(this.realProducts[0]));
        }

        cloneProduct(product, index) {
            const clone = product.cloneNode(true);
            clone.removeAttribute('data-product');
            clone.dataset.clone = index;
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button').forEach((element) => {
                element.tabIndex = -1;
            });
            // A copy's Add + Unlock still adds its product
            const button = clone.querySelector('[data-add]');
            button?.addEventListener('click', () => this.addProduct(button));
            return clone;
        }

        getLoopItems() {
            return Array.from(this.row.children).filter((item) => !item.hidden);
        }

        restingPosition(item) {
            return item.offsetLeft + item.offsetWidth - this.row.clientWidth;
        }

        getClosestItem() {
            let closest = null;
            let closestDistance = Infinity;
            this.getLoopItems().forEach((item) => {
                const distance = Math.abs(this.restingPosition(item) - this.row.scrollLeft);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closest = item;
                }
            });
            return closest;
        }

        jumpTo(item) {
            if (!item) return;
            this.row.scrollTo({ left: this.restingPosition(item), behavior: 'instant' });
        }

        // The arrow brings the next card (to the left) up against it, forever
        showNextProduct() {
            if (!this.row) return;
            const items = this.getLoopItems();
            const next = items[items.indexOf(this.getClosestItem()) + 1];
            if (next) this.row.scrollTo({ left: this.restingPosition(next), behavior: 'smooth' });
        }

        // Once the row stops on a copy, move to its real card
        onRowSettle() {
            const item = this.getClosestItem();
            if (!item || !item.hasAttribute('data-clone')) return;
            const real = this.realProducts[Number(item.dataset.clone)];
            if (!real) return;
            this.row.scrollTo({
                left: this.row.scrollLeft + this.restingPosition(real) - this.restingPosition(item),
                behavior: 'instant',
            });
        }

        /* State ---------------------------------------------------------- */

        setState(state) {
            if (this.dataset.state === state) return;
            this.dataset.state = state;
            if (state !== 'revealed') this.paintSurface();
            this.updateSurfaceFocus();
        }

        // Keyboard and screen reader users reveal with Enter or Space instead
        // of scratching, so the surface is focusable only while unlocked
        updateSurfaceFocus() {
            if (!this.canvas) return;
            const isUnlocked = this.dataset.state === 'unlocked';
            this.canvas.tabIndex = isUnlocked ? 0 : -1;
            this.canvas.setAttribute('aria-disabled', isUnlocked ? 'false' : 'true');
        }

        wasRevealed() {
            try {
                return window.localStorage.getItem(this.storageKey) === 'true';
            } catch (error) {
                return false;
            }
        }

        reveal() {
            if (this.dataset.state !== 'unlocked') return;
            this.setState('revealed');
            if (this.isPreview) return;
            try {
                window.localStorage.setItem(this.storageKey, 'true');
            } catch (error) {
                console.error('Error saving the GWP reveal:', error);
            }
        }

        /* Scratch surface ------------------------------------------------ */

        setUpScratch() {
            if (!this.canvas || !this.scratch) return;
            this.context = this.canvas.getContext('2d', { willReadFrequently: true });

            this.resizeObserver = new ResizeObserver(() => this.paintSurface());
            this.resizeObserver.observe(this.scratch);

            this.canvas.addEventListener('pointerdown', (event) => this.startScratch(event));
            this.canvas.addEventListener('pointermove', (event) => this.moveScratch(event));
            ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => {
                this.canvas.addEventListener(type, () => this.endScratch());
            });
            this.canvas.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                this.reveal();
            });
        }

        // Fills the canvas with the scheme's accent (Button hover background)
        paintSurface() {
            if (!this.context || this.dataset.state === 'revealed') return;
            const { width, height } = this.scratch.getBoundingClientRect();
            if (!width || !height) return;

            const ratio = window.devicePixelRatio || 1;
            this.canvas.width = Math.round(width * ratio);
            this.canvas.height = Math.round(height * ratio);

            const accent = getComputedStyle(this.scratch).getPropertyValue('--color-button-hover').trim();
            this.context.globalCompositeOperation = 'source-over';
            this.context.fillStyle = accent ? `rgb(${accent})` : '#d7c6e6';
            this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);

            this.scratch.classList.add('is-ready');
            this.scratch.classList.remove('is-scratching');
        }

        startScratch(event) {
            if (this.dataset.state !== 'unlocked') return;
            this.isScratching = true;
            this.moveCount = 0;
            this.lastPoint = this.getPoint(event);
            this.canvas.setPointerCapture?.(event.pointerId);
            this.scratch.classList.add('is-scratching');
            this.scratchTo(this.lastPoint);
        }

        moveScratch(event) {
            if (!this.isScratching) return;
            const point = this.getPoint(event);
            this.scratchTo(point);
            this.lastPoint = point;

            this.moveCount += 1;
            if (this.moveCount % 12 === 0) this.checkScratched();
        }

        endScratch() {
            if (!this.isScratching) return;
            this.isScratching = false;
            this.checkScratched();
        }

        getPoint(event) {
            const rect = this.canvas.getBoundingClientRect();
            const ratio = this.canvas.width / rect.width;
            return {
                x: (event.clientX - rect.left) * ratio,
                y: (event.clientY - rect.top) * ratio,
            };
        }

        scratchTo(point) {
            const ratio = this.canvas.width / this.canvas.getBoundingClientRect().width;
            const context = this.context;
            context.globalCompositeOperation = 'destination-out';
            context.lineCap = 'round';
            context.lineJoin = 'round';
            context.lineWidth = CsGwpScratch.BRUSH_SIZE * ratio;
            context.beginPath();
            context.moveTo(this.lastPoint.x, this.lastPoint.y);
            context.lineTo(point.x + 0.01, point.y);
            context.stroke();
        }

        // Samples every 32nd pixel to see how much of the surface is gone
        checkScratched() {
            const { width, height } = this.canvas;
            if (!width || !height) return;
            const pixels = this.context.getImageData(0, 0, width, height).data;
            let cleared = 0;
            let sampled = 0;
            for (let index = 3; index < pixels.length; index += 4 * 32) {
                sampled += 1;
                if (pixels[index] === 0) cleared += 1;
            }
            if (sampled && cleared / sampled >= CsGwpScratch.REVEAL_AT) this.reveal();
        }
    }

    customElements.define('cs-gwp-scratch', CsGwpScratch);
}
